import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './PlannerAdmin.css';

interface Customer {
  id: number;
  name: string;
}

interface Profile {
  id: number;
  animal_type: string;
  display_name: string;
  category: string;
  total_weeks: number;
}

interface BatchEntry {
  consumption_profile: number;
  quantity_started: number;
  arrival_date: string;
  breed: string;
  feed_line: string;
  packaging_type: string;
}

const CreatePlanAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [planName, setPlanName] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [cadence, setCadence] = useState('biweekly');
  const [deliveryMethod, setDeliveryMethod] = useState('milk_run');
  const [batches, setBatches] = useState<BatchEntry[]>([{
    consumption_profile: 0,
    quantity_started: 0,
    arrival_date: '',
    breed: '',
    feed_line: 'five_star',
    packaging_type: '40lb_bags'
  }]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, profilesRes] = await Promise.all([
        api.get('/customers/'),
        api.get('/planner/admin/feed-profiles/')
      ]);
      setCustomers(customersRes.data);
      setProfiles(profilesRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load required data');
    } finally {
      setLoading(false);
    }
  };

  const addBatch = () => {
    setBatches([...batches, {
      consumption_profile: 0,
      quantity_started: 0,
      arrival_date: '',
      breed: '',
      feed_line: 'five_star',
      packaging_type: '40lb_bags'
    }]);
  };

  const removeBatch = (index: number) => {
    if (batches.length > 1) {
      setBatches(batches.filter((_, i) => i !== index));
    }
  };

  const updateBatch = (index: number, field: keyof BatchEntry, value: any) => {
    const newBatches = [...batches];
    newBatches[index] = { ...newBatches[index], [field]: value };
    setBatches(newBatches);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId) {
      setError('Please select a customer');
      return;
    }

    const validBatches = batches.filter(b => b.consumption_profile && b.quantity_started > 0 && b.arrival_date);
    if (validBatches.length === 0) {
      setError('Please add at least one valid batch');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Create the plan
      const planResponse = await api.post('/planner/admin/plans/', {
        customer: customerId,
        name: planName || `${year} Seasonal Plan`,
        year,
        preferred_cadence: cadence,
        delivery_method: deliveryMethod,
        status: 'draft'
      });

      const planId = planResponse.data.id;

      // Create batches
      for (const batch of validBatches) {
        await api.post('/planner/admin/batches/', {
          seasonal_plan: planId,
          ...batch
        });
      }

      navigate(`/planner/plans/${planId}`);
    } catch (err: any) {
      console.error('Failed to create plan:', err);
      setError(err.response?.data?.error || 'Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="planner-admin-loading">Loading...</div>;
  }

  return (
    <div className="create-plan-admin-page">
      <div className="page-header">
        <h1>Create New Plan</h1>
        <Link to="/planner" className="btn-secondary">← Back</Link>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="create-plan-form">
        <div className="form-section">
          <h2>Plan Details</h2>

          <div className="form-group">
            <label>Customer *</label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(parseInt(e.target.value) || '')}
              required
            >
              <option value="">Select a customer...</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Plan Name</label>
              <input
                type="text"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder={`${year} Seasonal Plan`}
              />
            </div>

            <div className="form-group">
              <label>Year</label>
              <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
                <option value={2024}>2024</option>
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Delivery Cadence</label>
              <select value={cadence} onChange={(e) => setCadence(e.target.value)}>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div className="form-group">
              <label>Delivery Method</label>
              <select value={deliveryMethod} onChange={(e) => setDeliveryMethod(e.target.value)}>
                <option value="milk_run">Milk Run</option>
                <option value="pickup">Customer Pickup</option>
                <option value="ltl">LTL Freight</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <h2>Batches</h2>
            <button type="button" className="btn-small" onClick={addBatch}>
              + Add Batch
            </button>
          </div>

          {batches.map((batch, index) => (
            <div key={index} className="batch-entry">
              <div className="batch-header">
                <h4>Batch {index + 1}</h4>
                {batches.length > 1 && (
                  <button
                    type="button"
                    className="btn-small danger"
                    onClick={() => removeBatch(index)}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Animal Type *</label>
                  <select
                    value={batch.consumption_profile}
                    onChange={(e) => updateBatch(index, 'consumption_profile', parseInt(e.target.value))}
                    required
                  >
                    <option value={0}>Select...</option>
                    {profiles.map(p => (
                      <option key={p.id} value={p.id}>{p.display_name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Quantity *</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={batch.quantity_started || ''}
                    onChange={(e) => updateBatch(index, 'quantity_started', e.target.value as any)}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      updateBatch(index, 'quantity_started', Math.max(0, val));
                    }}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Arrival Date *</label>
                  <input
                    type="date"
                    value={batch.arrival_date}
                    onChange={(e) => updateBatch(index, 'arrival_date', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Breed (optional)</label>
                  <input
                    type="text"
                    value={batch.breed}
                    onChange={(e) => updateBatch(index, 'breed', e.target.value)}
                    placeholder="e.g., Freedom Rangers"
                  />
                </div>

                <div className="form-group">
                  <label>Feed Line</label>
                  <select
                    value={batch.feed_line}
                    onChange={(e) => updateBatch(index, 'feed_line', e.target.value)}
                  >
                    <option value="essentials">Essentials</option>
                    <option value="plus">Plus</option>
                    <option value="five_star">Five Star</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Packaging</label>
                  <select
                    value={batch.packaging_type}
                    onChange={(e) => updateBatch(index, 'packaging_type', e.target.value)}
                  >
                    <option value="40lb_bags">40 lb Bags</option>
                    <option value="1000lb_bulk">1000 lb Bulk</option>
                    <option value="2600lb_bulk">2600 lb Bulk</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="form-actions">
          <Link to="/planner" className="btn-secondary">Cancel</Link>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Creating...' : 'Create Plan'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreatePlanAdmin;
